<app-menu-bar></app-menu-bar>

<div class="dashboard-page">
<div class="dashboard-container">
<app-admin-nav></app-admin-nav>

<!-- The page waits as a whole rather than filling in around itself. Every
     field here reads off `order`, so rendering before it lands meant a box
     that sized itself to empty values and then jumped as they arrived. -->
@if (loading) {
    <div class="loading-state">
        <span class="spinner" role="status" [attr.aria-label]="'admin_loading' | translate"></span>
    </div>
} @else {
    <div id="order">
        <!-- The only way back to the list was to pick another tab in the admin
             nav, which loses where you were.

             The arrow is decorative — aria-hidden, so the label is not read out
             twice — and aria-label carries the same string as the visible text
             rather than a different one, so what is announced matches what is on
             screen. -->
        <button type="button" class="back-link" (click)="backToOrders()"
            [attr.aria-label]="'order_details_back' | translate">
            <i class="fa-light fa-angle-left" aria-hidden="true"></i>
            <span>{{ 'order_details_back' | translate }}</span>
        </button>

        <div class="w-100 box order-header">
            <div class="d-flex flex-column gap-2">
                <span class="orderNo">{{ 'order_details_order_no' | translate: { orderNo: order?.orderNo } }}</span>
                <span class="muted-header timestamps">{{ 'order_details_timestamps' | translate: {
                    created: (order?.createdAt | customDate), updated: (order?.updatedAt | customDate) } }}</span>
            </div>
            <div class="header-meta">
                <div class="d-flex flex-column gap-2">
                    <span class="muted-header">{{ 'orders_table_date' | translate }}</span>
                    <span class="value">{{ order?.createdAt | customDate }}</span>
                </div>
                <div class="d-flex flex-column gap-2">
                    <span class="muted-header">{{ 'orders_table_assigned_to' | translate }}</span>
                    <!-- Nothing at all until the order is in hand. The test used to be
                         `!order?.assignedTo`, which is true while the request is still
                         out — undefined is not assigned — so an order that turned out to
                         have an owner still offered the take button for the length of the
                         round trip. Local latency hid it; production did not. -->
                    @if (order) {
                        @if (order.assignedTo) {
                            <span class="value">{{ order.assignedTo }}</span>
                        } @else {
                            <!-- The label stays in the DOM while taking, hidden, so the button
                                 keeps its width; the spinner sits over it. -->
                            <button class="primary-btn take-btn" [disabled]="takingOrder"
                                [class.is-taking]="takingOrder" (click)="takeOrder(order)">
                                <span class="take-btn-label">{{ 'orders_action_take' | translate }}</span>
                                @if (takingOrder) {
                                    <span class="take-btn-spinner" aria-hidden="true"></span>
                                }
                            </button>
                        }
                    }
                </div>
                <div class="d-flex flex-column gap-2">
                    <span class="muted-header">{{ 'orders_table_status' | translate }}</span>
                    <!-- Unassigned orders show the status as plain text: it stays on
                         'New' until someone takes the order. -->
                    @if (order) {
                        @if (order.assignedTo) {
                            <!-- [ngModel] + (ngModelChange) rather than banana-in-a-box: the
                                 handler rolls the selection back if the save fails. -->
                            <ng-select #statusSelect class="fyes-select status-select" [items]="statuses" bindLabel="title"
                                bindValue="id"
                                [ngModel]="statusId" (ngModelChange)="onStatusChange($event)" [clearable]="false"
                                [searchable]="false" [disabled]="savingStatus">
                            </ng-select>
                        } @else {
                            <span class="value" [ngbTooltip]="'order_details_status_locked' | translate"
                                container="body">{{ currentStatusTitle }}</span>
                        }
                    }
                </div>
            </div>
        </div>
        <div class="order-columns">
            <div class="d-flex flex-column flex-grow-1 gap-3">
                <!-- Each box's title toggles its body, so on a phone the three
                     stacked boxes can be folded down instead of scrolled past.
                     The button's name is the title itself; aria-expanded says
                     which way it is, so no extra label is needed. -->
                <div class="box order-details">
                    <button type="button" class="box-toggle" (click)="expanded.contact = !expanded.contact"
                        [attr.aria-expanded]="expanded.contact" aria-controls="order-contact-body">
                        <span class="header">{{ 'order_details_contact_details' | translate }}</span>
                        <i class="fa-light fa-angle-down" aria-hidden="true"></i>
                    </button>
                    <div class="box-body" id="order-contact-body" [hidden]="!expanded.contact">
                    <div class="d-flex flex-column gap-2">
                        <span class="muted-header">{{ 'field_email' | translate }}</span>
                        <span class="value">{{ order?.contactDetails.email }}</span>
                    </div>
                    <div class="d-flex flex-column gap-2">
                        <span class="muted-header">{{ 'order_details_phone' | translate }}</span>
                        <span class="value">{{ order?.contactDetails.phone }}</span>
                    </div>
                    </div>
                </div>
                <div class="box order-details">
                    <button type="button" class="box-toggle" (click)="expanded.details = !expanded.details"
                        [attr.aria-expanded]="expanded.details" aria-controls="order-details-body">
                        <span class="header">{{ 'order_details_title' | translate }}</span>
                        <i class="fa-light fa-angle-down" aria-hidden="true"></i>
                    </button>
                    <div class="box-body" id="order-details-body" [hidden]="!expanded.details">
                    <div class="d-flex flex-column gap-2">
                        <span class="muted-header">{{ 'order_details_space_type' | translate }}</span>
                        <span class="value">{{ order?.spaceType }}</span>
                    </div>
                    <div class="d-flex flex-column gap-2">
                        <span class="muted-header">{{ 'order_details_size' | translate }}</span>
                        <span class="value">{{ order?.size }} {{ 'order_details_size_unit' | translate }}</span>
                    </div>
                    <!-- One row per question the space type asked, snapshotted onto
                         the order at creation so a later edit to the field
                         definitions cannot change what a past order says. -->
                    @for (row of order?.spaceDetailsSummary; track row.key) {
                    <div class="d-flex flex-column gap-2">
                        <!-- Rendered as stored: a free-text answer is already
                             filed as "Description" on the order, so there is no
                             per-field special case here. -->
                        <span class="muted-header">{{ row.label }}</span>
                        <!-- Free text can run to 500 characters and needs a readable
                             line-height, unlike the counts. -->
                        <span class="value" [class.long-value]="row.input === 'text'">{{ row.value }}</span>
                    </div>
                    }
                    <div class="d-flex flex-column gap-2">
                        <span class="muted-header">{{ 'order_details_automations' | translate }}</span>
                        <div class="d-flex gap-2 flex-wrap">
                            @for (automation of order?.automations; track $index) {
                            <div class="pill">
                                <span>{{automation.title}}</span>
                            </div>
                            }
                        </div>
                    </div>
                    <div class="price-area">
                        <div class="price-details">
                            <div class="d-flex justify-content-between gap-5">
                                <span class="muted-header">{{ 'order_details_item_subtotal' | translate }}</span>
                                <span class="muted-header">{{ order?.totalPrice }}</span>
                            </div>
                            <div class="d-flex justify-content-between">
                                <span class="muted-header">{{ 'order_details_discount' | translate }}</span>
                                <span class="muted-header">0</span>
                            </div>
                            <div class="d-flex justify-content-between">
                                <span class="value">{{ 'order_details_order_total' | translate }}</span>
                                <span class="value">{{ order?.totalPrice }}</span>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </div>

            <div class="comment-box box order-details">
                <button type="button" class="box-toggle" (click)="expanded.comments = !expanded.comments"
                    [attr.aria-expanded]="expanded.comments" aria-controls="order-comments-body">
                    <span class="header">{{ 'order_details_comments' | translate }}</span>
                    <i class="fa-light fa-angle-down" aria-hidden="true"></i>
                </button>
                <div class="box-body" id="order-comments-body" [hidden]="!expanded.comments">
                @for (comment of order?.comments; track $index) {
                <div class="comment">
                    <span class="text" style="color: #FFFFFF;">{{ comment.comment }}</span>
                    <span class="muted-text">{{ comment.date | customDate }}</span>
                    <span class="muted-text">{{ 'order_details_comment_by' | translate: { name: comment.commentedBy } }}</span>
                </div>
                }
                <div class="d-flex flex-column gap-2">
                    <span class="muted-header">{{ 'order_details_add_note' | translate }}</span>
                    <textarea rows="4" class="fyes-input" [placeholder]="'order_details_note_placeholder' | translate"
                        [(ngModel)]="newComment" (keydown.enter)="onEnterPress($event)"></textarea>
                </div>
                </div>
            </div>
        </div>
    </div>
}
</div>
</div>

<app-footer></app-footer>